import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiSettings, FiBell, FiChevronDown, FiMonitor, FiLogOut } from "react-icons/fi";
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from "react-icons/tb";
import { ROLE_LABELS, BACKEND_URL } from "../config";
import { allMenuPages } from './menuItems';

// Closes a dropdown when the user clicks anywhere outside `ref`.
const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [ref, onOutside]);
};

const Header = ({ isSidebarCollapsed, toggleSidebar }) => {
  const navigate = useNavigate();
  const { user, logout, canAccessPage } = useAuth();

  const [openMenu, setOpenMenu] = useState(null); // "profile" | "notifications" | null
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const menusRef = useRef(null);
  const searchRef = useRef(null);
  useClickOutside(menusRef, React.useCallback(() => setOpenMenu(null), []));
  useClickOutside(searchRef, React.useCallback(() => setSearchOpen(false), []));

  const toggleMenu = (name) => setOpenMenu(m => (m === name ? null : name));

  // Resolve display values
  const companyName = user?.company?.companyName || "Global";
  const userName    = user?.fullName || user?.name || "User";
  const userRole    = ROLE_LABELS[user?.role] || user?.role || "Member";

  // Company logo — use local file only (OneDrive URL is a viewer page, not a direct image)
  // Handles both "/uploads/companies/COMP-xxx.png" (new) and "COMP-xxx.png" (old cache) formats
  const buildLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('/')) return `${BACKEND_URL}${logo}`;
    return `${BACKEND_URL}/uploads/companies/${logo}`;
  };
  const companyLogo = buildLogoUrl(user?.company?.logo) || "/logo.png";
  const avatarSrc = user?.profileUpload ? `${BACKEND_URL}${user.profileUpload}` : companyLogo;

  // Page search — only pages this user can open, matched on name or group.
  const q = query.trim().toLowerCase();
  const results = q
    ? allMenuPages
        .filter(p => canAccessPage(p.key))
        .filter(p => `${p.group || ""} ${p.name}`.toLowerCase().includes(q))
        .slice(0, 8)
    : [];

  const goTo = (page) => {
    navigate(page.path);
    setQuery("");
    setSearchOpen(false);
  };

  const onSearchKey = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => (i + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(i => (i - 1 + results.length) % results.length); }
    else if (e.key === "Enter") { e.preventDefault(); goTo(results[activeIndex] || results[0]); }
    else if (e.key === "Escape") { setSearchOpen(false); }
  };

  return (
    // While a header dropdown is open the header must sit above the page content
    // (which normally sits above the header so page modals can cover it) — see Layout.css.
    <header className={`header ${isSidebarCollapsed ? 'collapsed' : ''} ${openMenu || (searchOpen && q) ? 'has-popover' : ''}`}>
      <div className="header-left">
        <button className="header-icon-btn" onClick={toggleSidebar}
          title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
          {isSidebarCollapsed ? <TbLayoutSidebarLeftExpand size={22} /> : <TbLayoutSidebarLeftCollapse size={22} />}
        </button>

        <div className="header-search" ref={searchRef}>
          <FiSearch size={17} className="header-search-icon" />
          <input
            type="text"
            placeholder="Search for IOEN"
            value={query}
            onChange={e => { setQuery(e.target.value); setSearchOpen(true); setActiveIndex(0); }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={onSearchKey}
          />
          {searchOpen && q && (
            <div className="header-search-results">
              {results.length === 0 && <div className="header-search-empty">No pages match "{query}"</div>}
              {results.map((p, i) => (
                <button key={p.path} className={i === activeIndex ? "active" : ""}
                  onMouseEnter={() => setActiveIndex(i)} onClick={() => goTo(p)}>
                  <span className="header-search-result-icon">{p.icon}</span>
                  <span>{p.name}</span>
                  {p.group && <span className="header-search-result-group">{p.group}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="header-right" ref={menusRef}>
        <button className="header-icon-btn" title="Settings — active devices" onClick={() => navigate('/active-devices')}>
          <FiSettings size={20} />
        </button>

        <div className="header-menu-wrap">
          <button className="header-icon-btn" title="Notifications" onClick={() => toggleMenu("notifications")}>
            <FiBell size={20} />
          </button>
          {openMenu === "notifications" && (
            <div className="header-dropdown header-notifications">
              <div className="header-dropdown-title">Notifications</div>
              <div className="header-dropdown-empty">You're all caught up.</div>
            </div>
          )}
        </div>

        <div className="header-menu-wrap">
          <button className="header-profile" onClick={() => toggleMenu("profile")} title="Account">
            <img src={avatarSrc} alt={userName}
              className={`header-profile-avatar ${user?.profileUpload ? "" : "is-logo"}`}
              onError={e => { e.target.onerror = null; e.target.src = '/logo.png'; }} />
            <span className="header-profile-text">
              <span className="header-profile-name">{userName}</span>
              <span className="header-profile-role">{userRole}</span>
            </span>
            <FiChevronDown size={18} className={`header-profile-chevron ${openMenu === "profile" ? "open" : ""}`} />
          </button>
          {openMenu === "profile" && (
            <div className="header-dropdown">
              <div className="header-dropdown-user">
                <span className="info-company">{companyName}</span>
                <span className="header-profile-name">{userName}</span>
                <span className="header-profile-role">{userRole}</span>
              </div>
              <button className="header-dropdown-item" onClick={() => { setOpenMenu(null); navigate('/active-devices'); }}>
                <FiMonitor size={15} /> Active Devices
              </button>
              <button className="header-dropdown-item danger" onClick={() => { setOpenMenu(null); logout(); }}>
                <FiLogOut size={15} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
